<template>
  <div>
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <div class="content-header-title">
          <h2 class="content-title">Lista de Contactos</h2>
          <p class="content-subtitle">Gestiona los contactos de las empresas</p>
        </div>
      </div>
      <div class="content-header-right">
        <b-button
          variant="primary"
          @click="$router.push({ name: 'contactos-nuevo' })"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          Nuevo Contacto
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <b-card class="mb-2">
      <b-row>
        <b-col md="4">
          <b-form-group label="Buscar por nombre">
            <b-form-input
              v-model="filters.nombre"
              placeholder="Nombre del contacto"
              @input="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Buscar por cargo">
            <b-form-input
              v-model="filters.cargo"
              placeholder="Cargo del contacto"
              @input="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Empresa">
            <v-select
              v-model="filters.empresa"
              :options="empresasOptions"
              placeholder="Seleccionar empresa"
              :reduce="opt => opt.value"
              @input="onFilterChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card>
      <div class="table-responsive">
        <b-table
          :items="contactosFiltrados"
          :fields="fields"
          :busy="loading"
          striped
          hover
          responsive
        >
          <!-- Loading slot -->
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong> Cargando...</strong>
            </div>
          </template>

          <!-- Nombre column -->
          <template #cell(nombre)="row">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(row.item.nombre)"
                variant="primary"
                class="mr-2"
              />
              <div>
                <div class="font-weight-bold">{{ row.item.nombre }}</div>
                <small class="text-muted">{{ row.item.cargo || 'Sin cargo' }}</small>
              </div>
            </div>
          </template>

          <!-- Empresa column -->
          <template #cell(empresa)="row">
            <span v-if="row.item.empresa && row.item.empresa.nombre">{{ row.item.empresa.nombre }}</span>
            <span v-else-if="row.item.nombreEmpresa">{{ row.item.nombreEmpresa }}</span>
            <span v-else class="text-muted">Sin empresa</span>
          </template>

          <!-- Contacto column -->
          <template #cell(contacto)="row">
            <div v-if="row.item.telefono || row.item.email">
              <div v-if="row.item.telefono" class="mb-1">
                <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                {{ row.item.telefono }}
              </div>
              <div v-if="row.item.email">
                <feather-icon icon="MailIcon" size="14" class="mr-1" />
                {{ row.item.email }}
              </div>
            </div>
            <span v-else class="text-muted">Sin contacto</span>
          </template>

          <!-- LinkedIn column -->
          <template #cell(linkedin)="row">
            <a
              v-if="row.item.linkedin"
              :href="row.item.linkedin"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary"
            >
              <feather-icon icon="ExternalLinkIcon" size="14" />
            </a>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Actions column -->
          <template #cell(actions)="row">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              boundary="window"
              offset="0,8"
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" />
              </template>
              <b-dropdown-item @click="verDetalle(row.item)">
                <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                Ver Detalle
              </b-dropdown-item>
              <b-dropdown-item @click="editarContacto(row.item)">
                <feather-icon icon="EditIcon" size="14" class="mr-50" />
                Editar
              </b-dropdown-item>
              <b-dropdown-item 
                v-if="row.item.email" 
                @click="enviarCorreo(row.item)"
              >
                <feather-icon icon="MailIcon" size="14" class="mr-50" />
                Enviar Correo
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item
                variant="danger"
                @click="eliminarContacto(row.item)"
              >
                <feather-icon icon="TrashIcon" size="14" class="mr-50" />
                Eliminar
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-2">
        <div>
          <span class="text-muted">
            Mostrando {{ contactos.length }} de {{ totalContactos }} contactos
          </span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalContactos"
          :per-page="perPage"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <!-- Modal para enviar correo -->
    <b-modal
      id="modal-enviar-correo"
      ref="modalEnviarCorreo"
      title="Enviar Correo"
      size="lg"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      @show="onModalShow"
      @hidden="onModalHidden"
    >
      <div v-if="contactoSeleccionado">
        <b-alert variant="info" show>
          <strong>Destinatario:</strong> {{ contactoSeleccionado.nombre }} ({{ contactoSeleccionado.email }})
        </b-alert>
        
        <!-- Redacción libre si no hay plantilla seleccionada -->
        <b-card v-if="!plantillaSeleccionada" class="mb-2" body-class="pt-1">
          <b-alert variant="secondary" show class="py-1 mb-2">
            Redacción libre (sin plantilla)
          </b-alert>
          <b-form-group label="Asunto" label-for="asunto-libre">
            <b-form-input id="asunto-libre" v-model="asuntoLibre" placeholder="Asunto del correo" />
          </b-form-group>
          <b-form-group label="Contenido">
            <quill-editor v-model="contenidoLibre" :options="editorOptions" style="height: 220px;" />
          </b-form-group>
          <b-form-group label="Adjuntos (opcional)">
            <input
              type="file"
              multiple
              class="form-control"
              @change="onFilesSelectedLibre"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip"
            />
            <div v-if="adjuntosLibres.length" class="mt-2">
              <small class="text-muted">{{ adjuntosLibres.length }} archivo(s) seleccionados</small>
              <ul class="mb-0 mt-1">
                <li v-for="(f, i) in adjuntosLibres" :key="i">{{ f.name }}</li>
              </ul>
              <b-button size="sm" variant="outline-danger" class="mt-1" @click="limpiarAdjuntosLibre">Limpiar</b-button>
            </div>
          </b-form-group>
        </b-card>

        <b-tabs content-class="mt-2">
          <!-- Tab de Plantilla -->
          <b-tab title="Plantilla" active>
            <b-form-group label="Seleccionar Plantilla">
              <v-select
                v-model="plantillaSeleccionada"
                :options="plantillasActivas"
                label="nombre"
                placeholder="Seleccionar plantilla..."
                @input="onPlantillaSeleccionada"
              />
            </b-form-group>
          </b-tab>

          <!-- Tab de Variables -->
          <b-tab title="Variables" :disabled="!plantillaSeleccionada">
            <div v-if="variablesPlantilla.length > 0">
              <b-form-group
                v-for="v in variablesPlantilla"
                :key="v.id"
                :label="v.label || v.key"
                :label-for="`var_${v.id}`"
              >
                <b-form-input
                  :id="`var_${v.id}`"
                  v-model="valoresVariables[v.key]"
                  :placeholder="`Valor para ${v.label || v.key}`"
                />
              </b-form-group>
            </div>
            <b-alert v-else variant="info" show>
              Esta plantilla no tiene variables personalizables.
            </b-alert>
          </b-tab>

          <!-- Tab de Adjuntos -->
          <b-tab title="Adjuntos" :disabled="!plantillaSeleccionada">
            <b-form-group label="Archivos Adjuntos">
              <input
                ref="fileInput"
                type="file"
                multiple
                class="form-control"
                @change="onFilesSelected"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              />
              <small class="form-text text-muted">
                Puedes seleccionar múltiples archivos. Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG
              </small>
            </b-form-group>

            <div v-if="archivosAdjuntos.length > 0" class="mt-3">
              <h6>Archivos seleccionados:</h6>
              <div class="d-flex flex-wrap gap-2">
                <b-badge
                  v-for="(archivo, index) in archivosAdjuntos"
                  :key="index"
                  variant="secondary"
                  class="d-flex align-items-center"
                >
                  {{ archivo.name }}
                  <feather-icon
                    icon="XIcon"
                    size="12"
                    class="ml-1 cursor-pointer"
                    @click="eliminarArchivo(index)"
                  />
                </b-badge>
              </div>
              <b-button
                variant="outline-danger"
                size="sm"
                class="mt-2"
                @click="limpiarArchivos"
              >
                Limpiar todos
              </b-button>
            </div>
          </b-tab>

          <!-- Tab de Vista Previa -->
          <b-tab title="Vista Previa" :disabled="!plantillaSeleccionada">
            <div v-if="plantillaSeleccionada">
              <h6>Asunto:</h6>
              <p class="border p-2 rounded">{{ asuntoProcesado }}</p>
              
              <h6>Contenido:</h6>
              <div class="border p-2 rounded" v-html="contenidoProcesado"></div>
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="cerrarModal">
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          @click="procesarEnvio"
          :disabled="enviando || !contactoSeleccionado || (!plantillaSeleccionada && (!asuntoLibre || !contenidoLibre))"
        >
          <b-spinner v-if="enviando" small class="mr-1" />
          {{ enviando ? 'Enviando...' : 'Enviar Correo' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import { ContactoService } from '@/services/contactoService'
import { EmpresaService } from '@/services/empresaService'
import plantillaCorreoService from '@/services/plantillaCorreoService'

export default {
  name: 'ContactosListaOptions',
  components: {
    vSelect,
    quillEditor,
  },
  data() {
    return {
      contactos: [],
      loading: false,
      currentPage: 1,
      perPage: 10,
      totalContactos: 0,
      empresasOptions: [],
      filters: {
        nombre: '',
        cargo: '',
        empresa: null,
      },
      // Variables para envío de correos
      contactoSeleccionado: null,
      plantillasActivas: [],
      plantillaSeleccionada: null,
      variablesPlantilla: [],
      valoresVariables: {},
      archivosAdjuntos: [],
      enviando: false,
      asuntoLibre: '',
      contenidoLibre: '',
      adjuntosLibres: [],
      editorOptions: {
        theme: 'snow',
        placeholder: 'Escribe el contenido del correo aquí...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            { 'header': 1 }, { 'header': 2 },
            { 'list': 'ordered'}, { 'list': 'bullet' },
            { 'indent': '-1'}, { 'indent': '+1' },
            { 'color': [] }, { 'background': [] },
            { 'align': [] },
            ['clean'],
            ['link']
          ]
        }
      },
      fields: [
        { key: 'nombre', label: 'Contacto', sortable: true },
        { key: 'empresa', label: 'Empresa', sortable: true },
        { key: 'contacto', label: 'Contacto' },
        { key: 'linkedin', label: 'LinkedIn' },
        { key: 'actions', label: 'Acciones' },
      ]
    }
  },
  mounted() {
    this.cargarContactos()
    this.cargarEmpresas()
    // Abrir modal de envío si viene desde detalle
    const q = this.$route.query || {}
    if (q.enviarCorreo === 'true' && q.contactoId && q.email) {
      const id = parseInt(q.contactoId)
      const seleccionado = this.contactos.find(c => c.idContacto === id) || { idContacto: id, email: q.email, nombre: q.nombre }
      this.enviarCorreo(seleccionado)
    }
  },
  methods: {
    formatUrl(url) {
      if (!url) return ''
      const trimmed = String(url).trim()
      if (/^https?:\/\//i.test(trimmed)) return trimmed
      return `https://${trimmed}`
    },
    getInitials(name) {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },

    async cargarContactos() {
      this.loading = true
      try {
        // Llamada al API
        const contactosData = await ContactoService.getAll()
        this.contactos = contactosData || []
        this.totalContactos = contactosData?.length || 0
      } catch (error) {
        this.contactos = []
        this.totalContactos = 0
      } finally {
        this.loading = false
      }
    },

    async cargarEmpresas() {
      try {
        const empresas = await EmpresaService.getAll()
        this.empresasOptions = (empresas || []).map(e => ({ value: e.idEmpresa, label: e.nombre }))
      } catch (error) {
        this.empresasOptions = []
      }
    },

    onFilterChange() {
      // Implementar filtrado
    },

    onPageChange(page) {
      this.currentPage = page
      this.cargarContactos()
    },

    verDetalle(contacto) {
      this.$router.push({ name: 'contactos-detalle', params: { id: contacto.idContacto } })
    },

    editarContacto(contacto) {
      this.$router.push({ name: 'contactos-editar', params: { id: contacto.idContacto } })
    },

    eliminarContacto(contacto) {
      this.$swal({
        title: '¿Estás seguro?',
        text: `¿Deseas eliminar el contacto "${contacto.nombre}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        try {
          this.loading = true
          await ContactoService.delete(contacto.idContacto)
          // Actualizar la lista en memoria sin recargar toda la tabla
          this.contactos = this.contactos.filter(c => c.idContacto !== contacto.idContacto)
          this.totalContactos = this.contactos.length
          this.$toast && this.$toast.success('Contacto eliminado correctamente')
        } catch (error) {
          this.$toast && this.$toast.error('No se pudo eliminar el contacto')
          // Como fallback, recargar el listado
          try { await this.cargarContactos() } catch (_) {}
        } finally {
          this.loading = false
        }
      })
    },

    // Métodos para envío de correos
    async enviarCorreo(contacto) {
      this.contactoSeleccionado = contacto
      await this.cargarPlantillasActivas()
      this.$refs.modalEnviarCorreo.show()
    },

    async cargarPlantillasActivas() {
      try {
        const plantillas = await plantillaCorreoService.getAllPlantillas()
        this.plantillasActivas = plantillas.filter(p => p.activa)
      } catch (error) {
        this.plantillasActivas = []
      }
    },

    onModalShow() {
      this.plantillaSeleccionada = null
      this.variablesPlantilla = []
      this.valoresVariables = {}
      this.archivosAdjuntos = []
    },

    onModalHidden() {
      this.contactoSeleccionado = null
      this.plantillaSeleccionada = null
      this.variablesPlantilla = []
      this.valoresVariables = {}
      this.archivosAdjuntos = []
    },

    onPlantillaSeleccionada(plantilla) {
      this.variablesPlantilla = []
      this.valoresVariables = {}
      if (!plantilla) return

      if (Array.isArray(plantilla.variables) && plantilla.variables.length > 0) {
        if (typeof plantilla.variables[0] === 'string') {
          const normalizadas = plantilla.variables
            .map(v => (typeof v === 'string' ? v.trim() : ''))
            .filter(v => v && v.length > 0)
          this.variablesPlantilla = normalizadas.map((nombre, idx) => ({ id: `${nombre}_${idx}`, key: nombre, label: nombre }))
          normalizadas.forEach(nombre => { this.valoresVariables[nombre] = '' })
        } else {
          this.variablesPlantilla = (plantilla.variables || [])
            .map((obj, idx) => {
              const key = obj?.nombreVariable || obj?.nombre
              return key ? ({ id: `${key}_${idx}`, key, label: key, descripcion: obj.descripcion }) : null
            })
            .filter(Boolean)
          this.variablesPlantilla.forEach(v => { this.valoresVariables[v.key] = '' })
        }
        return
      }

      if (typeof plantilla.variablesDisponibles === 'string' && plantilla.variablesDisponibles.trim().length > 0) {
        try {
          const vars = JSON.parse(plantilla.variablesDisponibles)
          if (Array.isArray(vars)) {
            const normalizadas = vars
              .map(v => (typeof v === 'string' ? v.trim() : ''))
              .filter(v => v && v.length > 0)
            this.variablesPlantilla = normalizadas.map((nombre, idx) => ({ id: `${nombre}_${idx}`, key: nombre, label: nombre }))
            normalizadas.forEach(nombre => { this.valoresVariables[nombre] = '' })
          }
        } catch (error) {
          this.variablesPlantilla = []
        }
      }
    },

    onFilesSelected(event) {
      const files = Array.from(event.target.files)
      this.archivosAdjuntos = [...this.archivosAdjuntos, ...files]
    },

    eliminarArchivo(index) {
      this.archivosAdjuntos.splice(index, 1)
    },

    limpiarArchivos() {
      this.archivosAdjuntos = []
      this.$refs.fileInput.value = ''
    },

    procesarTexto(texto, variables) {
      if (!texto || !variables) return texto
      
      let textoProcesado = texto
      Object.keys(variables).forEach(key => {
        const valor = variables[key] || `{{${key}}}`
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
        textoProcesado = textoProcesado.replace(regex, valor)
      })
      
      return textoProcesado
    },

    async procesarEnvio() {
      if (!this.contactoSeleccionado) return
      this.enviando = true
      try {
        let resultado
        const destinatarios = [this.contactoSeleccionado.email]
        if (this.plantillaSeleccionada) {
          const formData = new FormData()
          formData.append('idPlantilla', this.plantillaSeleccionada.idPlantilla)
          formData.append('valoresVariables', JSON.stringify(this.valoresVariables))
          formData.append('destinatarios', JSON.stringify(destinatarios))
          // adjuntos opcionales en este flujo (si los hubiera integrados aquí)
          resultado = await plantillaCorreoService.procesarPlantillaConAdjuntos(formData)
        } else {
          if (this.adjuntosLibres.length > 0) {
            const formData = new FormData()
            formData.append('asunto', this.asuntoLibre || '')
            formData.append('contenidoHtml', this.contenidoLibre || '')
            formData.append('destinatarios', JSON.stringify(destinatarios))
            this.adjuntosLibres.forEach(f => formData.append('adjuntos', f))
            resultado = await plantillaCorreoService.enviarCorreoLibreConAdjuntos(formData)
          } else {
            resultado = await plantillaCorreoService.enviarCorreoLibre({
              asunto: this.asuntoLibre || '',
              contenidoHtml: this.contenidoLibre || '',
              destinatarios,
            })
          }
        }
        this.$toast && this.$toast.success('Correo enviado correctamente')
        this.cerrarModal()
      } catch (error) {
        this.$toast && this.$toast.error('Error al enviar el correo')
      } finally {
        this.enviando = false
      }
    },

    onFilesSelectedLibre(e) {
      const files = Array.from(e.target.files || [])
      this.adjuntosLibres = files
    },

    limpiarAdjuntosLibre() {
      this.adjuntosLibres = []
    },

    cerrarModal() {
      this.$refs.modalEnviarCorreo.hide()
    }
  },

  computed: {
    contactosFiltrados() {
      const nombre = (this.filters.nombre || '').toLowerCase().trim()
      const cargo = (this.filters.cargo || '').toLowerCase().trim()
      const idEmpresa = this.filters.empresa || null
      return (this.contactos || []).filter(c => {
        const okNombre = !nombre || (c.nombre || '').toLowerCase().includes(nombre)
        const okCargo = !cargo || (c.cargo || '').toLowerCase().includes(cargo)
        const okEmpresa = !idEmpresa || c.idEmpresa === idEmpresa || c.empresa?.idEmpresa === idEmpresa
        return okNombre && okCargo && okEmpresa
      })
    },
    asuntoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      return this.procesarTexto(this.plantillaSeleccionada.asunto, this.valoresVariables)
    },

    contenidoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      return this.procesarTexto(this.plantillaSeleccionada.contenidoHtml, this.valoresVariables)
    }
  }
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.content-header-title h2 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.content-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.table-responsive {
  overflow-x: auto;
}
</style>
